import type { stats } from "@app/domain";
import type { SeatInfo } from "@app/protocol";
import { type ReactNode, type RefObject, useEffect, useId, useRef } from "react";
import { type CardAction, PlayerCardBody } from "./PlayerCard";

/**
 * A seat's player card on the table (review 0017 S5, U2): beside the pod, not modal, so the hand
 * and the panels stay live. Escape, a tap anywhere else (a card played, a bid made) or the close
 * button shut it, and focus goes back to the pod.
 */
export function PodCard({
  seat,
  rank,
  action,
  style,
  dense,
  opener,
  onClose,
  social,
}: {
  seat: SeatInfo;
  rank: stats.RankFrame | null;
  action?: CardAction | undefined;
  style: Record<string, string | number>;
  dense: boolean;
  opener: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  /** Gifts and hiding, when the card opens at the table (review 0022). */
  social?: ReactNode;
}) {
  const labelId = useId();
  const box = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  // Focus returns to the pod when the keyboard closed the card, not when a tap went elsewhere.
  const refocus = useRef(true);
  useEffect(() => {
    heading.current?.focus();
    const back = opener.current;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (box.current?.contains(target) || back?.contains(target)) return;
      refocus.current = false;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer, true);
      if (refocus.current) back?.focus({ preventScroll: true });
    };
  }, [onClose, opener]);
  return (
    <div
      ref={box}
      role="dialog"
      aria-labelledby={labelId}
      className={`absolute z-30 rounded-[18px] border-[1.5px] border-edge bg-panel-solid text-start text-ink shadow-panel backdrop-blur-[16px] motion-safe:animate-rise ${dense ? "w-[228px] p-3" : "w-[272px] p-4"}`}
      style={style}
    >
      <PlayerCardBody
        member={seat}
        rank={rank}
        labelId={labelId}
        headingRef={heading}
        action={action}
        onClose={onClose}
      />
      {social}
    </div>
  );
}
